import { getCurrentRange } from "@circle-react-shared/uikit/TipTapBlockEditor/Toolbar/helpers";
import { AlignmentButton } from "@circle-react-uikit/TipTapBlockEditor/CTAExtension/AlignmentButton";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useSmOrMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { Fragment } from "@tiptap/pm/model";
import { NodeSelection } from "@tiptap/pm/state";
import type { Editor } from "@tiptap/react";

import { usePaywallSettingsModal } from "./PaywallSettingsModal";

interface PaywallOptionsProps {
  deleteNode: () => void;
  editor: Editor;
  getPos: () => number | undefined;
  selected: boolean;
}

export const PaywallOptions = ({
  editor,
  deleteNode,
  getPos,
  selected,
}: PaywallOptionsProps) => {
  const paywallSettingsModal = usePaywallSettingsModal();
  const isMobile = useSmOrMdScreenMediaQuery();

  let isFirst = false;
  let isLast = false;
  const pos = getPos();

  if (pos !== undefined) {
    const $pos = editor.state.doc.resolve(pos);
    const parent = $pos.parent;
    const index = $pos.index();

    const hasTrailingEmptyParagraph =
      parent.lastChild?.type.name === "paragraph" &&
      parent.lastChild.content.size === 0;

    isFirst = index === 0;
    isLast = index >= parent.childCount - (hasTrailingEmptyParagraph ? 2 : 1);
  }

  const moveNodeUp = () => {
    const pos = getPos();
    if (pos === undefined) return;

    const { state } = editor;
    const { doc, tr } = state;
    const $pos = doc.resolve(pos);
    const nodeBefore = $pos.nodeBefore;
    const currentNode = doc.nodeAt(pos);

    if (!nodeBefore || !currentNode) return;

    const startPos = pos - nodeBefore.nodeSize;
    editor.view.dispatch(
      tr
        .replaceWith(
          startPos,
          pos + currentNode.nodeSize,
          Fragment.fromArray([currentNode, nodeBefore]),
        )
        .setSelection(NodeSelection.create(tr.doc, startPos)),
    );
  };

  const moveNodeDown = () => {
    const pos = getPos();
    if (pos === undefined) return;

    const { state } = editor;
    const { doc, tr } = state;
    const currentNode = doc.nodeAt(pos);
    if (!currentNode) return;

    const afterPos = pos + currentNode.nodeSize;
    const nodeAfter = doc.nodeAt(afterPos);
    if (!nodeAfter) return;

    editor.view.dispatch(
      tr
        .replaceWith(
          pos,
          afterPos + nodeAfter.nodeSize,
          Fragment.fromArray([nodeAfter, currentNode]),
        )
        .setSelection(NodeSelection.create(tr.doc, pos + nodeAfter.nodeSize)),
    );
  };

  return (
    <div
      className={classNames(
        "absolute right-0 top-0 flex w-full -translate-y-full items-center justify-end pb-1",
        { "invisible group-hover:visible": !isMobile && !selected },
      )}
    >
      <div className="bg-text-editor-modifier shadow-md flex items-center gap-x-1 overflow-hidden rounded-md p-1">
        <AlignmentButton
          iconType="16-arrow-up"
          onClick={moveNodeUp}
          disabled={isFirst}
        />
        <AlignmentButton
          iconType="16-arrow-down"
          onClick={moveNodeDown}
          disabled={isLast}
        />
        <span
          className="inline-block h-5 w-px bg-[rgba(255,255,255,0.2)]"
          aria-hidden
        />
        <AlignmentButton
          iconType="16-edit"
          onClick={() =>
            paywallSettingsModal.show({
              editor,
              range: getCurrentRange({ editor }),
            })
          }
        />
        <AlignmentButton onClick={deleteNode} iconType="16-trash" />
      </div>
    </div>
  );
};
